import { useCallback, useMemo } from 'react';
import { useHits, useSearchBox, useInstantSearch, useConfigure } from 'react-instantsearch';
import { Hit } from 'instantsearch.js';

interface SearchResult extends Hit {
  objectID: string;
  name?: string;
  status?: string;
  _documentPath?: string;
  searchResultURL?: string;
  collectionName?: string;
}

export type CollectionFilter = 'all' | 'records' | 'formVersions';

interface UseSearchOptions {
  filter?: string;
  collectionFilter?: CollectionFilter;
}

/**
 * Hook for searching generic records with optional index and filter configuration
 * @param options Optional configuration for index and filters
 * @returns Search results and controls
 */
export const useSearch = (options?: UseSearchOptions) => {
  const { collectionFilter, filter } = options ?? {};
  const { refine, query, clear } = useSearchBox();
  const { items } = useHits<SearchResult>();
  const { status, error, refresh } = useInstantSearch();

  const combinedFilters = useMemo(() => {
    const collectionFilterStr = collectionFilter && collectionFilter !== 'all'
      ? `_collectionName:${collectionFilter}`
      : '';
    return [collectionFilterStr, filter].filter(Boolean).join(' AND ');
  }, [collectionFilter, filter]);

  useConfigure({
    filters: combinedFilters
  });

  const refreshSearch = useCallback(() => {
    refresh();
  }, [refresh]);

  return {
    searchResults: items,
    searchQuery: query,
    setSearchQuery: refine,
    clearSearch: clear,
    refreshSearch,
    isLoading: status === 'loading',
    error: error?.message || null,
  };
};